import { Navigate, Route, Routes } from 'react-router-dom';
import { RequireAuth } from './components/auth/RequireAuth';
import { Layout } from './components/chrome/Layout';
import { isPublicIntakeEnabled } from './features/intake/publicIntakeFeatureFlag';
import { RequirePatientAuth } from './features/portal/auth/RequirePatientAuth';
import { PortalLayout } from './features/portal/chrome/PortalLayout';
import { isSelfRescheduleEnabled } from './features/reschedule/featureFlag';
import { lazyRoute } from './lib/lazyRoute';

// Each page is its own chunk. Guards and layouts stay eager so the chrome can
// paint while a page loads. Public intake and reschedule stay in the build as
// their own chunks (issues #149 / #150); the flag only decides whether the route
// is registered, so an off flag never requests the chunk.
const AdminPage = lazyRoute('AdminPage', () => import('./features/admin/AdminPage'));
const SignInPage = lazyRoute('SignInPage', () => import('./features/auth/SignInPage'));
const BillingPage = lazyRoute('BillingPage', () => import('./features/billing/BillingPage'));
const PublicIntakePage = lazyRoute('PublicIntakePage', () => import('./features/intake/PublicIntakePage'));
const PatientChartPage = lazyRoute('PatientChartPage', () => import('./features/patients/PatientChartPage'));
const PatientsRosterPage = lazyRoute('PatientsRosterPage', () => import('./features/patients/PatientsRosterPage'));
const PortalResetPasswordPage = lazyRoute(
  'PortalResetPasswordPage',
  () => import('./features/portal/auth/PortalResetPasswordPage')
);
const PortalSignInPage = lazyRoute('PortalSignInPage', () => import('./features/portal/auth/PortalSignInPage'));
const PortalAppointmentsPage = lazyRoute(
  'PortalAppointmentsPage',
  () => import('./features/portal/pages/AppointmentsPage')
);
const PortalBillingPage = lazyRoute('PortalBillingPage', () => import('./features/portal/pages/BillingPage'));
const PortalDocumentsPage = lazyRoute('PortalDocumentsPage', () => import('./features/portal/pages/DocumentsPage'));
const PortalHealthPage = lazyRoute('PortalHealthPage', () => import('./features/portal/pages/HealthPage'));
const PortalMessagesPage = lazyRoute('PortalMessagesPage', () => import('./features/portal/pages/MessagesPage'));
const PortalProfilePage = lazyRoute('PortalProfilePage', () => import('./features/portal/pages/ProfilePage'));
const PortalHomePage = lazyRoute('PortalHomePage', () => import('./features/portal/PortalHomePage'));
const ReportsPage = lazyRoute('ReportsPage', () => import('./features/reports/ReportsPage'));
const SelfReschedulePage = lazyRoute('SelfReschedulePage', () => import('./features/reschedule/SelfReschedulePage'));
const SchedulePage = lazyRoute('SchedulePage', () => import('./features/schedule/SchedulePage'));
const TodayPage = lazyRoute('TodayPage', () => import('./features/today/TodayPage'));
const VisitPage = lazyRoute('VisitPage', () => import('./features/visit/VisitPage'));

/**
 * Router for the SPEC §10.2 route map. Public `/signin`; everything else sits
 * behind `RequireAuth` inside the ACTO chrome `Layout`. Assumes a Router provider
 * above it (see `main.tsx`; tests mount a MemoryRouter).
 */
export function App() {
  return (
    <Routes>
      <Route path="/signin" element={<SignInPage />} />
      {/*
       * Public, tokenized self-service reschedule/cancel (issue #149). Gated OFF by default: the public
       * execution path (anonymous ClientApplication / server proxy) is deferred to #166 and atomic slot
       * reservation+rollback to #167, so until #166 lands we don't register a working-looking public link.
       * The page + verifying bot stay in the build; flip VITE_ENABLE_SELF_RESCHEDULE=true to enable.
       */}
      {isSelfRescheduleEnabled() ? <Route path="/reschedule" element={<SelfReschedulePage />} /> : null}

      {/*
       * Public, tokenized new-patient intake (issues #150, #177 §4). The page posts the signed token +
       * answers to the #166 execution proxy, which invokes the token-verifying `intake-submit` bot. Still
       * gated OFF by default: the route registers only when VITE_ENABLE_PUBLIC_INTAKE=true, i.e. once the
       * bot + proxy settings are deployed (infra/azure/README.md, 'Public intake submit').
       */}
      {isPublicIntakeEnabled() ? <Route path="/intake" element={<PublicIntakePage />} /> : null}

      {/*
       * Patient portal (issues #153/#154) — a separate `/portal` route space in this same app,
       * strictly isolated from the clinician UI. Public portal auth pages sit outside the guard;
       * everything under `RequirePatientAuth` requires a signed-in PATIENT profile (a clinician
       * session is bounced out) inside the patient-only `PortalLayout` chrome. DATA isolation is
       * enforced server-side by the patient AccessPolicy (see features/portal/auth/patientAccessPolicy).
       */}
      <Route path="/portal/signin" element={<PortalSignInPage />} />
      <Route path="/portal/reset-password" element={<PortalResetPasswordPage />} />
      <Route element={<RequirePatientAuth />}>
        <Route element={<PortalLayout />}>
          <Route path="/portal" element={<PortalHomePage />} />
          <Route path="/portal/appointments" element={<PortalAppointmentsPage />} />
          <Route path="/portal/messages" element={<PortalMessagesPage />} />
          <Route path="/portal/profile" element={<PortalProfilePage />} />
          <Route path="/portal/health" element={<PortalHealthPage />} />
          <Route path="/portal/documents" element={<PortalDocumentsPage />} />
          <Route path="/portal/billing" element={<PortalBillingPage />} />
          <Route path="/portal/*" element={<Navigate to="/portal" replace />} />
        </Route>
      </Route>

      <Route element={<RequireAuth />}>
        <Route element={<Layout />}>
          <Route index element={<Navigate to="/today" replace />} />
          <Route path="/today" element={<TodayPage />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/patients" element={<PatientsRosterPage />} />
          <Route path="/patients/:id" element={<PatientChartPage />} />
          <Route path="/visit/:id" element={<VisitPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Route>
      </Route>
    </Routes>
  );
}

export default App;
